import React, { useState, useEffect, useCallback } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button, Input, Label, FormGroup } from 'reactstrap';
import Autocomplete from '@material-ui/lab/Autocomplete';
import TextField from '@material-ui/core/TextField';
import { withStyles, createStyles, WithStyles } from '@material-ui/core/styles';
import { useOrganizationStore } from '../../store/organization.store';
import { generateClaimCode } from '../../types/license';

// Styles matching ShareLicenseModal pattern
const styles = () => createStyles({
  autocomplete: {
    width: '100%',
    '& .MuiInputBase-root': {
      backgroundColor: 'transparent',
      color: '#fff',
      minHeight: '32px',
      fontFamily: 'var(--font-family-primary)',
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: 'rgba(255, 255, 255, 0.2) !important',
        borderWidth: '1px'
      },
    },
    '& input': {
      padding: '4px 8px',
      fontSize: 'var(--menu-font-size)',
      fontFamily: 'var(--font-family-primary)',
      color: '#fff'
    },
    '& .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.1)'
    },
    '&:hover .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(255, 255, 255, 0.2)'
    },
    '& .MuiAutocomplete-clearIndicator, & .MuiAutocomplete-popupIndicator': {
      color: '#ffffff',
      '&:hover': {
        backgroundColor: 'rgba(255, 255, 255, 0.1)'
      }
    }
  },
});

// Share target interface (same as license system)
export interface ReviewShareTarget {
  type: 'org' | 'client' | 'agency';
  id: string;
  name: string;
  linkedOrgId: string | null;
  hasOrg: boolean;
}

// Output data from this modal
export interface ReviewShareData {
  shareTarget: ReviewShareTarget | null;
  reviewCode: string | null;
  notes: string;
  sharedAt: number;
}

interface ShareForReviewModalProps extends WithStyles<typeof styles> {
  isOpen: boolean;
  onClose: () => void;
  onApply: (reviewData: ReviewShareData) => void;
  trackName: string;
  trackId: string;
  firebase: any;
}

const ShareForReviewModal: React.FC<ShareForReviewModalProps> = ({
  isOpen,
  onClose,
  onApply,
  trackName,
  trackId,
  firebase,
  classes,
}) => {
  // Share target state
  const [shareTarget, setShareTarget] = useState<ReviewShareTarget | null>(null);
  const [shareTargetSearch, setShareTargetSearch] = useState('');
  const [shareTargetOptions, setShareTargetOptions] = useState<ReviewShareTarget[]>([]);
  const [isSearchingTargets, setIsSearchingTargets] = useState(false);

  // Notes
  const [notes, setNotes] = useState('');

  // Review link state
  const [generatedReviewCode, setGeneratedReviewCode] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);

  const { currentOrgId } = useOrganizationStore();

  // Reset state when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setShareTarget(null);
      setShareTargetSearch('');
      setShareTargetOptions([]);
      setNotes('');
      setGeneratedReviewCode(null);
      setCopied(false);
    }
  }, [isOpen]);

  // Debounced search for share targets
  const searchShareTargets = useCallback(
    async (searchTerm: string) => {
      if (!searchTerm || searchTerm.length < 2 || !firebase?.searchShareTargets) {
        setShareTargetOptions([]);
        return;
      }

      setIsSearchingTargets(true);
      try {
        const results = await firebase.searchShareTargets(searchTerm, currentOrgId);
        setShareTargetOptions(results);
      } catch (error) {
        console.error('Error searching share targets:', error);
        setShareTargetOptions([]);
      }
      setIsSearchingTargets(false);
    },
    [firebase, currentOrgId]
  );

  // Debounce the search
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      searchShareTargets(shareTargetSearch);
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [shareTargetSearch, searchShareTargets]);

  // Generate external review link
  const handleGenerateLink = () => {
    const code = generateClaimCode();
    setGeneratedReviewCode(code);
  };

  // Copy review link to clipboard
  const handleCopyLink = () => {
    if (!generatedReviewCode) return;
    const link = `${window.location.origin}/review/${generatedReviewCode}`;
    navigator.clipboard.writeText(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // Handle share — always generates a review code for link access
  const handleApply = () => {
    // Always create a review code so the studio can copy the link later
    const reviewCode = generatedReviewCode || generateClaimCode();

    const reviewData: ReviewShareData = {
      shareTarget: shareTarget || null,
      reviewCode: reviewCode,
      notes,
      sharedAt: Date.now(),
    };
    onApply(reviewData);
  };

  const handleClose = () => {
    onClose();
  };

  const isApplyDisabled = !shareTarget && !generatedReviewCode;

  return (
    <Modal
      isOpen={isOpen}
      toggle={handleClose}
      style={{
        maxWidth: '550px',
        margin: '1.75rem auto',
      }}
      contentClassName="delete-confirm-modal"
    >
      <ModalHeader
        toggle={handleClose}
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          color: '#ffffff',
          padding: '1rem 1.5rem',
          borderBottom: '1px solid rgba(255, 255, 255, 0.1)',
          fontSize: '1.25rem',
          fontWeight: '500',
        }}
        close={
          <button
            className="close"
            onClick={handleClose}
            style={{
              color: 'var(--accent-1-color)',
              opacity: 1,
              textShadow: 'none',
              background: 'none',
              border: 'none',
              fontSize: '1.75rem',
              fontWeight: 'normal',
              padding: '0.25rem',
              cursor: 'pointer',
            }}
          >
            &times;
          </button>
        }
      >
        <span style={{ color: '#fff' }}>Share for Review</span>
      </ModalHeader>

      <ModalBody
        style={{
          backgroundColor: 'var(--chatbot-color-1)',
          color: '#ffffff',
          padding: '1.5rem',
          fontSize: '1rem',
          lineHeight: '1.5',
          maxHeight: '70vh',
          overflowY: 'auto',
        }}
      >
        <p style={{ color: '#fff', marginBottom: '1.5rem' }}>
          Share "<strong>{trackName}</strong>" for client approval.
        </p>

        {/* Option A: Share With Organization (In-App) */}
        <div style={{
          marginBottom: '1.5rem',
          padding: '1rem',
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '8px',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <Label style={{ color: '#fff', fontWeight: '500', marginBottom: '0.5rem' }}>
            Share with Organization
          </Label>
          <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
            Search for a client or agency to share this spot for in-app review.
          </p>

          {shareTarget ? (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '0.5rem 0.75rem',
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              borderRadius: '6px',
              border: '1px solid rgba(255, 255, 255, 0.15)',
            }}>
              <div>
                <span style={{ color: '#fff', fontWeight: '500' }}>{shareTarget.name}</span>
                <span style={{
                  color: 'rgba(255, 255, 255, 0.5)',
                  fontSize: '0.8rem',
                  marginLeft: '0.5rem',
                }}>
                  {shareTarget.type === 'org' ? 'Organization' : shareTarget.type === 'client' ? 'Client' : 'Agency'}
                </span>
                <div style={{ fontSize: '0.8rem', color: shareTarget.hasOrg ? 'var(--accent-1-color)' : 'rgba(255,255,255,0.5)' }}>
                  {shareTarget.hasOrg ? 'Direct share' : 'Creates review link'}
                </div>
              </div>
              <button
                onClick={() => setShareTarget(null)}
                style={{
                  background: 'none',
                  border: 'none',
                  color: 'var(--accent-1-color)',
                  cursor: 'pointer',
                  fontSize: '1.25rem',
                  padding: '0 0.25rem',
                }}
              >
                &times;
              </button>
            </div>
          ) : (
            <Autocomplete
              options={shareTargetOptions}
              getOptionLabel={(option) => option.name || ''}
              inputValue={shareTargetSearch}
              onInputChange={(_, value) => setShareTargetSearch(value)}
              onChange={(_, value) => {
                setShareTarget(value);
                setShareTargetSearch('');
              }}
              loading={isSearchingTargets}
              noOptionsText={shareTargetSearch.length < 2 ? 'Type to search...' : 'No results found'}
              renderInput={(params) => (
                <TextField
                  {...params}
                  variant="outlined"
                  placeholder="Search organizations, clients, agencies..."
                  className={classes.autocomplete}
                  size="small"
                />
              )}
              renderOption={(option) => (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  <span style={{ color: '#fff', fontSize: 'var(--menu-font-size)', fontFamily: 'var(--font-family-primary)' }}>
                    {option.name}
                  </span>
                  <span style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.75rem' }}>
                    {option.type === 'org' ? 'Organization' : option.type === 'client' ? 'Client' : 'Agency'}
                    {option.hasOrg ? ' — Direct share' : ' — Creates review link'}
                  </span>
                </div>
              )}
              className={classes.autocomplete}
            />
          )}
        </div>

        {/* External Review Link — can be used alongside or instead of org share */}
        <div style={{
          marginBottom: '1.5rem',
          padding: '1rem',
          backgroundColor: 'rgba(255, 255, 255, 0.05)',
          borderRadius: '8px',
          border: '1px solid rgba(255, 255, 255, 0.1)'
        }}>
          <Label style={{ color: '#fff', fontWeight: '500', marginBottom: '0.5rem' }}>
            External Review Link
          </Label>
          <p style={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.85rem', marginBottom: '0.75rem' }}>
            Generate a secure link to send to external clients for review. No login required.
          </p>

          {generatedReviewCode ? (
            <div>
              <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.5rem',
                padding: '0.5rem 0.75rem',
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
                borderRadius: '6px',
                border: '1px solid rgba(255, 255, 255, 0.15)',
              }}>
                <span style={{
                  flex: 1,
                  color: 'var(--accent-1-color)',
                  fontSize: '0.85rem',
                  fontFamily: 'monospace',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}>
                  {`${window.location.origin}/review/${generatedReviewCode}`}
                </span>
                <Button
                  size="sm"
                  className={copied ? 'btn-success-custom' : 'btn-primary-custom'}
                  onClick={handleCopyLink}
                  style={{ minWidth: '70px' }}
                >
                  <span style={{ position: 'relative', zIndex: 2, color: '#fff' }}>
                    {copied ? 'Copied!' : 'Copy'}
                  </span>
                </Button>
              </div>
              <p style={{ color: 'rgba(255, 255, 255, 0.5)', fontSize: '0.8rem', marginTop: '0.5rem', marginBottom: 0 }}>
                This link will expire in 7 days. The recipient can listen and approve/reject without signing in.
              </p>
            </div>
          ) : (
            <Button
              className="btn-primary-custom"
              onClick={handleGenerateLink}
              size="sm"
            >
              <span style={{ position: 'relative', zIndex: 2, color: '#fff' }}>Generate Review Link</span>
            </Button>
          )}
        </div>

        {/* Notes */}
        <FormGroup style={{ marginBottom: '0' }}>
          <Label style={{ color: '#fff', fontWeight: '500' }}>Notes</Label>
          <Input
            type="textarea"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="Add notes for the reviewer..."
            rows={3}
            style={{
              backgroundColor: 'rgba(255, 255, 255, 0.1)',
              border: '1px solid rgba(255, 255, 255, 0.2)',
              color: '#fff',
              borderRadius: '4px',
              resize: 'vertical',
            }}
          />
        </FormGroup>
      </ModalBody>

      <ModalFooter
        style={{
          backgroundColor: 'var(--chatbot-color-2)',
          borderTop: '1px solid rgba(255, 255, 255, 0.1)',
          padding: '1rem 1.5rem',
          display: 'flex',
          justifyContent: 'flex-end',
          gap: '1rem',
        }}
      >
        <Button
          className="btn-info-custom"
          onClick={handleApply}
          disabled={isApplyDisabled}
          style={isApplyDisabled ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
        >
          <span style={{ position: 'relative', zIndex: 2 }}>
            {shareTarget
              ? (shareTarget.hasOrg ? 'Share for Review' : 'Share & Get Link')
              : generatedReviewCode
                ? 'Save Review Link'
                : 'Share for Review'
            }
          </span>
        </Button>
        <Button className="btn-danger-custom" onClick={handleClose}>
          <span style={{ position: 'relative', zIndex: 2 }}>Cancel</span>
        </Button>
      </ModalFooter>
    </Modal>
  );
};

export default withStyles(styles)(ShareForReviewModal);
